// src/components/CompleteRegistration.js (imports)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CompleteRegistration.css';
import { auth } from '../firebase-config';


// API helpers
import { apiFetch } from '../apiFetch';



const DEBUG = process.env.NODE_ENV !== 'production';




// Inline list so the dropdown always works (no API/import needed)
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina",
  "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados",
  "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana",
  "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon",
  "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros",
  "Congo (Congo-Brazzaville)", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
  "Democratic Republic of the Congo", "Denmark", "Djibouti", "Dominica", "Dominican Republic",
  "Ecuador", "Egypt", "El Salvador", "England", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini",
  "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana",
  "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras",
  "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
  "Ivory Coast", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait",
  "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein",
  "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta",
  "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco",
  "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar (Burma)", "Namibia", "Nauru",
  "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea",
  "North Macedonia", "Norway", "Northern Ireland", "Oman", "Pakistan", "Palau", "Palestine",
  "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar",
  "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
  "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia",
  "Scotland", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia",
  "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka",
  "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania",
  "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey",
  "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United States", "Uruguay",
  "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Wales", "Yemen", "Zambia", "Zimbabwe"
];


const normalizeCity = (city = '') =>
  city.trim().toLowerCase().replace(/\s+/g, ' ');


const CompleteRegistration = () => {
  useEffect(() => {
    // clear any leftover roles or IDs so no one skips registration
    localStorage.removeItem("userRole");
    localStorage.removeItem("role");
    localStorage.removeItem("agentId");
    localStorage.removeItem("scoutId");
    localStorage.removeItem("clubId");
    localStorage.removeItem("playerId");
  }, []);

  const [formData, setFormData] = useState({
    // General
    firstName: '',
    lastName: '',
    username: '',
    dob: '',
    country: '', // Country of Residence
    city: '',    // City of Residence
    nationality: '', // Nationality (passport)
    role: '',

    // Agent fields
    agencyName: '',
    agencyWebsite: '',
    countryOfOperation: '',
    contactEmail: '',
    agentTransfermarktUrl: '',

    // Scout fields (existing + NEW)
    currentEmployer: '',
    scoutingExperience: '',
    website: '',

    roleAtOrganization: '',        // NEW
    cityBase: '',                  // NEW
    regionsLeagues: '',            // NEW (comma-separated)
    yearsOfScouting: '',           // NEW (number)
    scoutingQualifications: '',    // NEW (comma-separated)
    ageGroups: '',                 // NEW (comma-separated)
    positions: '',                 // NEW (comma-separated)

    // Club fields
    clubName: '',
    clubWebsite: '',
    clubCountry: '',
    currentLeague: '',
    typeOfClub: '',
    clubEmail: '',
    clubCity: '',        // NEW – City / Base
    clubAgeGroups: '',   // NEW – Age Groups / Teams Run


    // Player fields
    playerPosition: '',
    playerClub: '',
    gender: '',
    emailAddress: '',
    currentClub: '',
    previousClub: '',
    status: '',
    primaryPosition: '',
    secondaryPosition: '',
    preferredFoot: '',
    height: '',
    weight: '',
    playerStrength: '',
  });



  const [termsAccepted, setTermsAccepted] = useState(false);
  const [step, setStep] = useState(1);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [usernameStatus, setUsernameStatus] = useState({
    checking: false,
    available: null, // true | false | null
    message: '',
    suggestions: [],
  });



  useEffect(() => {
    let alive = true;

    // 1) Show static list immediately (no waiting on network)
    try {
      const cleanedStatic = [...new Set((COUNTRIES || []).map(String))]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));

      if (alive) {
        setCountries(cleanedStatic);
        setErrorMessage("");
        setLoading(false); // ✅ enable the <select> right away
        if (DEBUG) console.log("[countries] using STATIC first; count:", cleanedStatic.length);
      }
    } catch (e) {
      console.error("[countries] static list failed:", e);
    }

    // 2) Try to refresh from API in the background (optional)
    (async () => {
      try {
        const url = "/countries";
        if (DEBUG) console.log("[countries] background refresh from:", url);

        const res = await apiFetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });

        if (!res.ok) throw new Error(`countries fetch failed (${res.status})`);
        const payload = await res.json();


        let list = [];
        if (Array.isArray(payload)) list = payload;
        else if (payload && Array.isArray(payload.countries)) list = payload.countries;
        else if (payload && Array.isArray(payload.data)) {
          list = payload.data.map(x => typeof x === "string" ? x : x?.name || x?.country || "");
        }

        if (Array.isArray(list) && list.length > 0) {
          const cleaned = [...new Set(list.map(String))]
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
          if (alive) {
            setCountries(cleaned);
            setErrorMessage("");
            if (DEBUG) console.log("[countries] refreshed from API; count:", cleaned.length);
          }
        } else {
          console.warn("[countries] API returned empty/invalid list; keeping static.");
        }
      } catch (err) {
        console.warn("[countries] background refresh failed; keeping static.", err);
      }
    })();

    return () => { alive = false; };
  }, []);



  useEffect(() => {
    if (step !== 2) return;
    if (!formData.username || formData.username.length < 3) {
      setUsernameStatus({
        checking: false,
        available: null,
        message: '',
        suggestions: [],
      });
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        setUsernameStatus(prev => ({ ...prev, checking: true }));

        const res = await apiFetch(
          `/username/check?username=${encodeURIComponent(formData.username)}`,
          {
            method: "GET",
            headers: { Accept: "application/json" },
          }
        );


        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.message || data?.error || "Unable to check username");
        }

        setUsernameStatus({
          checking: false,
          available: data.available,
          message: data.message,
          suggestions: data.suggestions || [],
        });



      } catch (err) {
        setUsernameStatus({
          checking: false,
          available: false,
          message: 'Unable to check username',
          suggestions: [],
        });
      }
    }, 500); // ⏱ debounce (500ms)

    return () => clearTimeout(timeout);
  }, [formData.username, step]);





  const navigate = useNavigate();  // Initialize navigate hook

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (['username', 'height', 'weight'].includes(name)) {
      setErrorMessage('');
    }
  };


  const handleTermsChange = () => {
    setTermsAccepted(!termsAccepted);
  };


  console.log("🔥 SUBMIT CLICKED");
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (DEBUG) console.log("🚀 handleSubmit triggered");


    const fullName = `${formData.firstName} ${formData.lastName}`;

    if (!termsAccepted) {
      if (DEBUG) console.log("❌ Terms not accepted");

      setErrorMessage('You must accept the terms of service to complete registration.');
      return;
    }

    const formattedDob = formData.dob ? formData.dob.split('T')[0] : null;

    const firebaseUid = auth.currentUser?.uid;
    const loginEmail = auth.currentUser?.email;


    if (!firebaseUid) {
      setErrorMessage('Authentication error. Please log in again.');
      return;
    }

    if (!loginEmail) {
      setErrorMessage('Authentication email missing. Please log in again.');
      return;
    }


    const registrationData = {
      fullName,
      username: formData.username.trim().toLowerCase(),
      dob: formattedDob,
      country: formData.country,
      residenceCity: normalizeCity(formData.city),
      nationality: formData.nationality,
      role: formData.role.toLowerCase(),
    };






    if (formData.role === 'Agent') {
      registrationData.agencyName = formData.agencyName;
      registrationData.countryOfOperation = formData.countryOfOperation;
      registrationData.contactEmail = formData.contactEmail;

      //Optional Fields
      if (formData.agencyWebsite?.trim()) {
        registrationData.agencyWebsite = formData.agencyWebsite.trim();
      }

      if (formData.agentTransfermarktUrl?.trim()) {
        registrationData.agentTransfermarktUrl = formData.agentTransfermarktUrl.trim();
      }


    } else if (formData.role === 'Scout') {
      registrationData.contactEmail = formData.contactEmail;
      registrationData.countryOfOperation = formData.countryOfOperation;
      registrationData.currentEmployer = formData.currentEmployer;
      registrationData.roleAtOrganization = formData.roleAtOrganization;
      registrationData.cityBase = formData.cityBase;
      registrationData.regionsLeagues = formData.regionsLeagues;
      registrationData.yearsOfScouting = Number(formData.yearsOfScouting);
      registrationData.ageGroups = formData.ageGroups;
      registrationData.positions = formData.positions;
      if (formData.scoutingQualifications?.trim()) {
        registrationData.scoutingQualifications = formData.scoutingQualifications.trim();
      }

      if (formData.scoutingExperience?.trim()) {
        registrationData.scoutingExperience = formData.scoutingExperience.trim();
      }

      if (formData.website?.trim()) {
        registrationData.website = formData.website.trim();
      }


    } else if (formData.role === 'Club') {
      registrationData.clubName = formData.clubName;
      registrationData.clubCountry = formData.clubCountry;
      registrationData.currentLeague = formData.currentLeague;
      registrationData.typeOfClub = formData.typeOfClub;
      registrationData.clubEmail = formData.clubEmail;
      registrationData.clubCity = formData.clubCity;
      registrationData.clubAgeGroups = formData.clubAgeGroups;

      if (formData.clubWebsite?.trim()) {
        registrationData.clubWebsite = formData.clubWebsite.trim();
      }


    } else if (formData.role === 'Player') {
      registrationData.gender = formData.gender;
      registrationData.emailAddress = formData.emailAddress;
      registrationData.currentClub = formData.currentClub;
      registrationData.clubCountry = formData.clubCountry;
      registrationData.previousClub = formData.previousClub;
      registrationData.status = formData.status;
      registrationData.primaryPosition = formData.primaryPosition;
      registrationData.secondaryPosition = formData.secondaryPosition;
      registrationData.preferredFoot = formData.preferredFoot;
      registrationData.height = Number(formData.height);
      registrationData.weight = Number(formData.weight);
      registrationData.playerStrength = formData.playerStrength;

    } else {
      if (DEBUG) console.log("❌ Invalid role selected");
      setErrorMessage('Invalid role selected. Please choose either Agent, Scout, Club, or Player.');
      return;
    }

    try {

      if (DEBUG) console.log("📦 Sending registrationData:", registrationData);

     
      const res = await apiFetch("/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(registrationData),
      });


      // Try to parse JSON either way (success or error)
      let data = null;
      try {
        data = await res.json();
      } catch (e) {
        data = null;
      }

      // If backend returned an error status, show its message
      if (!res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          `Registration failed (${res.status})`;
        console.error("❌ Registration failed:", msg, data);
        setErrorMessage(msg);
        return;
      }

      if (!data || !data.userId) {
        console.error("❌ Backend did not return userId.", data);
        setErrorMessage("Registration failed. No user ID returned.");
        return;
      }

      const { userId } = data;

      if (!userId) {
        console.error("❌ userId is undefined", data);
        setErrorMessage("Registration failed. Invalid user ID.");
        return;
      }

      setSuccessMessage("Registration successful! Redirecting...");


      // Normalize role and store IDs for RoleDashboard
      const normalizedRole = String(formData.role || '').toLowerCase();

      if (normalizedRole === 'agent') {
        localStorage.setItem('agentId', userId);
      } else if (normalizedRole === 'scout') {
        localStorage.setItem('scoutId', userId);
      } else if (normalizedRole === 'club') {
        localStorage.setItem('clubId', userId);
      } else if (normalizedRole === 'player') {
        localStorage.setItem('playerId', userId);
      }

      localStorage.setItem('role', normalizedRole);
      localStorage.setItem('userRole', normalizedRole);

      // ✅ FIXED: direct role-based routing (NO /dashboard)
      switch (normalizedRole) {
        case 'player':
          navigate('/player-dashboard', { replace: true });
          break;
        case 'scout':
          navigate('/scout-dashboard', { replace: true });
          break;
        case 'club':
          navigate('/club-dashboard', { replace: true });
          break;
        case 'agent':
        default:
          navigate('/agent-dashboard', { replace: true });
          break;
      }

    } catch (error) {
      console.error("❌ Request failed:", error);
      setErrorMessage(error?.message || "Registration failed");
    }

  };




  const handleNext = () => {
    if (step === 1 && (!formData.firstName || !formData.lastName)) {
      setErrorMessage('Please provide your first name and last name.');
      return;
    }

    if (step === 2) {
      if (!formData.username) {
        setErrorMessage('Please provide your username.');
        return;
      }

      if (usernameStatus.checking) {
        setErrorMessage('Please wait while we check your username.');
        return;
      }

      if (usernameStatus.available === false) {
        setErrorMessage('This username is already taken.');
        return;
      }

      if (usernameStatus.available === null) {
        setErrorMessage('Please wait for username verification.');
        return;
      }
    }


    if (step === 3 && !formData.dob) {
      setErrorMessage('Please provide your date of birth.');
      return;
    }

    if (
      step === 4 &&
      (!formData.country || !formData.city || !formData.nationality)
    ) {
      setErrorMessage('Please provide your country, city, and nationality.');
      return;
    }


    if (step === 5 && !formData.role) {
      setErrorMessage('Please select your role.');
      return;
    }

    // ✅ UPDATED: Validate Scout-only fields at Step 6
    if (step === 6 && formData.role === 'Scout') {
      const requiredScoutFields = [
        'contactEmail',
        'countryOfOperation',
        'currentEmployer',
        'roleAtOrganization',
        'regionsLeagues',
        'yearsOfScouting',
        'ageGroups',
        'positions'
      ];


      for (const field of requiredScoutFields) {
        if (!String(formData[field] || '').trim()) {
          setErrorMessage('Please fill out all required scout fields.');
          return;
        }
      }
    }

    if (step === 6 && formData.role === 'Player' && !formData.gender) {
      setErrorMessage('Please select your gender.');
      return;
    }




    // ✅ Validate Player height & weight at Step 6
    if (step === 6 && formData.role === 'Player') {
      const height = Number(formData.height);
      const weight = Number(formData.weight);

      if (!Number.isFinite(height) || height < 1.3 || height > 2.3) {
        setErrorMessage('Height must be between 1.30m and 2.30m.');
        return;
      }

      if (!Number.isFinite(weight) || weight < 40 || weight > 150) {
        setErrorMessage('Weight must be between 40kg and 150kg.');
        return;
      }
    }


    // All good → go to next step
    setStep(step + 1);
    setErrorMessage('');
  };

  return (
    <div className="complete-registration-container">

      {/* Logo Header */}
      <div className="registration-header">
        <img
          src="/images/VSN-Icon-White-Cropped.png"
          alt="Virtual Scouting Network"
          className="registration-logo"
        />
      </div>

      <form onSubmit={handleSubmit}>

        {/* Card Header */}
        <div className="registration-card-header">
          <span className="step-badge">Step {step} of 7</span>

          <h2 className="registration-title">
            Complete Your Registration
          </h2>

          <p className="registration-subtitle">
            {step === 1 && "Enter your full name"}
            {step === 2 && "Choose a username"}
            {step === 3 && "Enter your date of birth"}
            {step === 4 && "Enter your location details"}
            {step === 5 && "Select your role"}
            {step === 6 && "Provide role-specific details"}
            {step === 7 && "Review and accept terms"}
          </p>

          <div className="registration-divider"></div>
        </div>



        {/* General Questions */}
        {step === 1 && (
          <div>
            <div className="name-container">
              <input
                type="text"
                id="firstName"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                id="lastName"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <label htmlFor="username">Username</label>

            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              autoComplete="off"
            />

            {/* 🔄 Checking */}
            {usernameStatus.checking && (
              <div style={{ color: '#666', marginTop: '6px' }}>
                Checking availability…
              </div>
            )}

            {/* ⚠️ Taken */}
            {!usernameStatus.checking && usernameStatus.available === false && (
              <div style={{ color: '#d97706', marginTop: '6px' }}>
                ⚠️ {usernameStatus.message || 'Username already taken'}

                {usernameStatus.suggestions?.length > 0 && (
                  <div style={{ fontSize: '13px', marginTop: '4px' }}>
                    Suggestions: {usernameStatus.suggestions.join(', ')}
                  </div>
                )}
              </div>
            )}


            {/* ✅ Available */}
            {!usernameStatus.checking && usernameStatus.available === true && (
              <div style={{ color: 'green', marginTop: '6px' }}>
                ✓ Username is available
              </div>
            )}
          </div>
        )}


        {step === 3 && (
          <div>
            <label htmlFor="dob">Date of Birth</label>
            <input
              type="date"
              id="dob"
              name="dob"
              value={formData.dob}
              onChange={handleChange}
              required
            />
          </div>
        )}

        {step === 4 && (
          <div>
            {/* Country of Residence */}
            <label htmlFor="country">
              Country of Residence {Array.isArray(countries) ? `(${countries.length})` : ""}
            </label>

            <select
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              disabled={loading}
              style={{ color: '#000', background: '#fff' }}
            >
              {loading ? (
                <option value="">Loading countries…</option>
              ) : Array.isArray(countries) && countries.length > 0 ? (
                <>
                  <option value="">Select country…</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </>
              ) : (
                <option value="">No countries available</option>
              )}
            </select>

            {/* City of Residence */}
            <label htmlFor="city" style={{ marginTop: 12 }}>
              City of Residence (Nearest)
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              placeholder="e.g. Nakuru"
            />

            {/* Nationality */}
            <label htmlFor="nationality" style={{ marginTop: 12 }}>
              Nationality
            </label>
            <select
              id="nationality"
              name="nationality"
              value={formData.nationality}
              onChange={handleChange}
              required
              style={{ color: '#000', background: '#fff' }}
            >
              <option value="">Select nationality…</option>
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>

          </div>
        )}








        {step === 5 && (
          <div>
            <label htmlFor="role">Role</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
            >
              <option value="">Select...</option>
              <option value="Agent">Agent</option>
              <option value="Scout">Scout</option>
              <option value="Club">Club</option>
              <option value="Player">Player</option>
            </select>
          </div>
        )}

        {/* Agent-Specific Questions */}
        {formData.role === 'Agent' && step === 6 && (
          <div>
            <label htmlFor="agencyName">What is Your Agency Name?</label>
            <input
              type="text"
              id="agencyName"
              name="agencyName"
              value={formData.agencyName}
              onChange={handleChange}
              required
            />

            <label htmlFor="agencyWebsite">
              Agency Website <span style={{ opacity: 0.6 }}>(optional)</span>
            </label>
            <input
              type="url"
              id="agencyWebsite"
              name="agencyWebsite"
              value={formData.agencyWebsite}
              onChange={handleChange}
            />


            <label htmlFor="countryOfOperation">Country of Operation:</label>
            <input
              type="text"
              id="countryOfOperation"
              name="countryOfOperation"
              value={formData.countryOfOperation}
              onChange={handleChange}
              required
            />

            <label htmlFor="agentTransfermarktUrl">
              Agent Transfermarkt Link <span style={{ opacity: 0.6 }}>(optional)</span>
            </label>
            <input
              type="url"
              id="agentTransfermarktUrl"
              name="agentTransfermarktUrl"
              value={formData.agentTransfermarktUrl}
              onChange={handleChange}
              placeholder="https://www.transfermarkt.com/..."
            />


            <label htmlFor="contactEmail">Enter your Contact Email Address:</label>
            <input
              type="email"
              id="contactEmail"
              name="contactEmail"
              value={formData.contactEmail}
              onChange={handleChange}
              required
            />
          </div>
        )}


        {/* Scout-Specific Questions */}
        {formData.role === 'Scout' && step === 6 && (
          <div className="scout-container">
            {/* LEFT COLUMN – 6 fields */}
            <div className="scout-column">
              <label htmlFor="countryOfOperation">Country of Operation:</label>
              <input
                type="text"
                id="countryOfOperation"
                name="countryOfOperation"
                value={formData.countryOfOperation}
                onChange={handleChange}
                required
              />

              <label htmlFor="currentEmployer">Current Employer:</label>
              <input
                type="text"
                id="currentEmployer"
                name="currentEmployer"
                value={formData.currentEmployer}
                onChange={handleChange}
                required
                placeholder="Ajax Youth Academy"
              />

              <label htmlFor="roleAtOrganization">Role at Organization:</label>
              <input
                type="text"
                id="roleAtOrganization"
                name="roleAtOrganization"
                value={formData.roleAtOrganization}
                onChange={handleChange}
                required
              />

              <label htmlFor="ageGroups">Age Groups You Scout:</label>
              <input
                type="text"
                id="ageGroups"
                name="ageGroups"
                value={formData.ageGroups}
                onChange={handleChange}
                required
                placeholder="13-17, 18-23, 24-28"
              />

              <label htmlFor="positions">Positions You Scout:</label>
              <input
                type="text"
                id="positions"
                name="positions"
                value={formData.positions}
                onChange={handleChange}
                required
                placeholder="Strikers, Wingers"
              />

              <label htmlFor="scoutingExperience">
                Scouting Experience <span style={{ opacity: 0.6 }}>(optional)</span>
              </label>

              <textarea
                id="scoutingExperience"
                name="scoutingExperience"
                value={formData.scoutingExperience}
                onChange={handleChange}
                placeholder="Manchester 2009-2015, Liverpool 2015-2022"
              />
            </div>

            {/* RIGHT COLUMN – 6 fields */}
            <div className="scout-column">
              <label htmlFor="contactEmail">Contact Email Address:</label>
              <input
                type="email"
                id="contactEmail"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                required
              />

              <label htmlFor="cityBase">City / Base</label>
              <input
                type="text"
                id="cityBase"
                name="cityBase"
                value={formData.cityBase}
                onChange={handleChange}
              />

              <label htmlFor="regionsLeagues">Leagues You Cover:</label>
              <input
                type="text"
                id="regionsLeagues"
                name="regionsLeagues"
                value={formData.regionsLeagues}
                onChange={handleChange}
                required
              />

              <label htmlFor="yearsOfScouting">Years of Scouting:</label>
              <input
                type="number"
                id="yearsOfScouting"
                name="yearsOfScouting"
                value={formData.yearsOfScouting}
                onChange={handleChange}
                required
              />

              <label htmlFor="scoutingQualifications">
                Scouting Qualifications <span style={{ opacity: 0.6 }}>(optional)</span>
              </label>

              <input
                type="text"
                id="scoutingQualifications"
                name="scoutingQualifications"
                value={formData.scoutingQualifications}
                onChange={handleChange}
                placeholder="UEFA Talent Identification Certificate"
              />

              <label htmlFor="website">
                Website / Portfolio <span style={{ opacity: 0.6 }}>(optional)</span>
              </label>

              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://www.vsnscouting.com"
              />
            </div>
          </div>
        )}





        {/* Club-Specific Questions */}
        {formData.role === 'Club' && step === 6 && (
          <div className="club-grid">

            {/* LEFT COLUMN */}
            <div className="club-column">
              <label htmlFor="clubName">Enter the Club Name:</label>
              <input
                type="text"
                id="clubName"
                name="clubName"
                value={formData.clubName}
                onChange={handleChange}
                required
              />

              <label htmlFor="clubCountry">Enter the Club Country:</label>
              <input
                type="text"
                id="clubCountry"
                name="clubCountry"
                value={formData.clubCountry}
                onChange={handleChange}
                required
              />

              <label htmlFor="clubCity">Enter the Club City / Base:</label>
              <input
                type="text"
                id="clubCity"
                name="clubCity"
                value={formData.clubCity}
                onChange={handleChange}
                required
              />

              <label htmlFor="currentLeague">Enter the Current League:</label>
              <input
                type="text"
                id="currentLeague"
                name="currentLeague"
                value={formData.currentLeague}
                onChange={handleChange}
                required
              />
            </div>

            {/* RIGHT COLUMN */}
            <div className="club-column">
              <label htmlFor="typeOfClub">Select the Type of Club:</label>
              <select
                id="typeOfClub"
                name="typeOfClub"
                value={formData.typeOfClub}
                onChange={handleChange}
                required
              >
                <option value="">Select...</option>
                <option value="Pro">Pro</option>
                <option value="Semi-Pro">Semi-Pro</option>
                <option value="Amateur">Amateur</option>
              </select>

              <label htmlFor="clubAgeGroups">Age Groups / Teams Run:</label>
              <input
                type="text"
                id="clubAgeGroups"
                name="clubAgeGroups"
                placeholder="e.g. First Team, U23, U19"
                value={formData.clubAgeGroups}
                onChange={handleChange}
                required
              />

              <label htmlFor="clubEmail">Enter the Club Email:</label>
              <input
                type="email"
                id="clubEmail"
                name="clubEmail"
                value={formData.clubEmail}
                onChange={handleChange}
                required
              />

              <label htmlFor="clubWebsite">
                Club Website <span style={{ opacity: 0.6 }}>(optional)</span>
              </label>
              <input
                type="url"
                id="clubWebsite"
                name="clubWebsite"
                value={formData.clubWebsite}
                onChange={handleChange}
              />

            </div>

          </div>
        )}









        {/* Player-Specific Questions */}
        {formData.role === 'Player' && step === 6 && (
          <div className="player-role-questions">
            <div className="player-form-columns">
              {/* Left Column */}
              <div className="player-column">

                <label htmlFor="gender">Gender:</label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>


                <label htmlFor="emailAddress">Enter Email Address:</label>
                <input
                  type="email"
                  id="emailAddress"
                  name="emailAddress"
                  value={formData.emailAddress}
                  onChange={handleChange}
                  required
                />

                <label htmlFor="currentClub">Enter your Current Club:</label>
                <input
                  type="text"
                  id="currentClub"
                  name="currentClub"
                  value={formData.currentClub}
                  onChange={handleChange}
                  required
                />

                <label htmlFor="clubCountry">Enter the Club Country:</label>
                <input
                  type="text"
                  id="clubCountry"
                  name="clubCountry"
                  value={formData.clubCountry}
                  onChange={handleChange}
                  required
                />

                <label htmlFor="previousClub">Previous Club:</label>
                <textarea
                  id="previousClub"
                  name="previousClub"
                  value={formData.previousClub}
                  onChange={handleChange}
                  placeholder="Optional (e.g. Liverpool - 2025)"
                  style={{
                    resize: 'vertical',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    padding: '10px',
                  }}
                />


                <label htmlFor="status">
                  Representation Status
                </label>

                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <option value="">Do you have an agent?</option>
                  <option value="Free Agent">Free Agent (No Agent)</option>
                  <option value="Represented">Represented (Has Agent)</option>
                </select>

              </div>

              {/* Right Column */}
              <div className="player-column">
                <label htmlFor="primaryPosition">Primary Position:</label>
                <select
                  id="primaryPosition"
                  name="primaryPosition"
                  value={formData.primaryPosition}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option> {/* Default "Select" option */}
                  <option value="GK">GK</option>
                  <option value="LB">LB</option>
                  <option value="RB">RB</option>
                  <option value="CB">CB</option>
                  <option value="CDM">CDM</option>
                  <option value="CM">CM</option>
                  <option value="AM">AM</option>
                  <option value="LW">LW</option>
                  <option value="RW">RW</option>
                  <option value="ST">ST</option>
                </select>

                <label htmlFor="secondaryPosition">Secondary Position:</label>
                <select
                  id="secondaryPosition"
                  name="secondaryPosition"
                  value={formData.secondaryPosition}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option> {/* Default "Select" option */}
                  <option value="GK">GK</option>
                  <option value="LB">LB</option>
                  <option value="RB">RB</option>
                  <option value="CB">CB</option>
                  <option value="CDM">CDM</option>
                  <option value="CM">CM</option>
                  <option value="AM">AM</option>
                  <option value="LW">LW</option>
                  <option value="RW">RW</option>
                  <option value="ST">ST</option>
                </select>

                <label htmlFor="preferredFoot">Preferred Foot:</label>
                <select
                  id="preferredFoot"
                  name="preferredFoot"
                  value={formData.preferredFoot}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  <option value="Left">Left</option>
                  <option value="Right">Right</option>
                </select>

                <label htmlFor="height">Enter your Height (m):</label>
                <input
                  type="number"
                  id="height"
                  name="height"
                  value={formData.height}
                  onChange={handleChange}
                  required
                  min="1.3"
                  max="2.3"
                  step="0.01"
                  placeholder="e.g. 1.78"
                  style={{
                    fontSize: '14px',
                    padding: '10px',
                  }}
                />


                <label htmlFor="weight">Enter your Weight (kg):</label>
                <input
                  type="number"
                  id="weight"
                  name="weight"
                  value={formData.weight}
                  onChange={handleChange}
                  required
                  min="40"
                  max="150"
                  step="1"
                  placeholder="e.g. 72"
                  style={{
                    fontSize: '14px',
                    padding: '10px',
                  }}
                />


                <label htmlFor="playerStrength">Player Strength:</label>
                <textarea
                  id="playerStrength"
                  name="playerStrength"
                  value={formData.playerStrength}
                  onChange={handleChange}
                  required
                  placeholder="Dribbling, etc."
                  style={{
                    resize: 'vertical',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    padding: '10px',
                  }}
                />
              </div>
            </div>
          </div>
        )}




        {/* Terms and Conditions Section */}
        {step === 7 && (
          <div className="terms-container">
            <div className="terms-content">
              <h4>Terms and Conditions</h4>
              <div className="terms-scrollable">
                <p>
                  By completing your registration, you agree to the following platform rules of <strong>Virtual Scouting Network (VSN)</strong>:
                  <br />
                  <br />
                  • VSN is a digital discovery, visibility, and communication platform and is not an agent, representative, employer, or a party to any football transfer, trial, or employment agreement.
                  <br />
                  • VSN does not own, represent, employ, or control players.
                  <br />
                  • All player profiles, videos, and interactions are accessible only within the VSN platform and are subject to platform rules.
                  <br />
                  • All communication between users must take place exclusively within the VSN platform. Bypassing the platform or sharing private contact details is strictly prohibited.
                  <br />
                  • Players may upload football-related videos via external links; VSN does not guarantee visibility, ratings, trials, contracts, or professional outcomes.
                  <br />
                  • Players must be at least 13 years old to use the platform. Players under 18 may only participate with parental or legal guardian supervision and consent.
                  <br />
                  • Agents, scouts, and clubs must maintain an active paid subscription to access their dashboards and platform features.
                  <br />
                  • Players are not required to pay a subscription to access the player dashboard.
                  <br />
                  • Recruiters may use the “Offer to Sign Player” feature to obtain time-limited platform exclusivity only.
                  <br />
                  • Platform exclusivity restricts other users from submitting competing platform offers during the active period but does not create ownership, representation, or real-world control.
                  <br />
                  • Signing a player on the platform does not guarantee trials, contracts, employment, or professional advancement.
                  <br />
                  • During an active platform signing period, players may not engage in underhand or bypass deals with other platform users.
                  <br />
                  • VSN reserves the right to suspend or terminate accounts that violate platform rules, safety requirements, or payment obligations.
                  <br />
                  • These terms are governed by the laws of the user’s country of residence.
                </p>
              </div>


              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="termsCheckbox"
                  checked={termsAccepted}
                  onChange={handleTermsChange}
                />
                <label htmlFor="termsCheckbox">I accept Terms and Conditions</label>
              </div>
            </div>
          </div>
        )}


        {errorMessage && (
          <div className="error-message">
            {errorMessage}
          </div>
        )}



        {/* Buttons */}
        {step < 7 && (
          <button
            type="button"
            onClick={handleNext}
            disabled={step === 2 && usernameStatus.checking}
            style={{
              opacity: step === 2 && usernameStatus.checking ? 0.6 : 1,
              cursor: step === 2 && usernameStatus.checking ? 'not-allowed' : 'pointer',
            }}
          >
            {step === 2 && usernameStatus.checking ? 'Checking…' : 'Continue →'}
          </button>
        )}


        {step === 7 && <button type="submit">Submit</button>}
      </form>

      {successMessage && <div className="success-message">{successMessage}</div>}
    </div>
  );
};

export default CompleteRegistration;
